import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { COMPANY } from "../../site/data";

/* 디지털 명함 관리 — /<slug>_businesscard 명함 등록 · 사진 · QR · 연락처 파일 */
const EMPTY = { slug: "", family_name: "", given_name: "", title: "", phone: "", email: "", active: true };

function cardUrl(slug) {
  return `${COMPANY.origin}/${slug}_businesscard`;
}

async function copy(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    window.prompt("복사해서 쓰세요", text);
    return false;
  }
}

function CardForm({ initial, onSave, onCancel, busy }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(f); }}
      className="sstoa-form"
      style={{ marginTop: 12 }}
    >
      <div className="sstoa-form-cols">
        <div className="sstoa-field">
          <label>성 *</label>
          <input className="sstoa-input" required value={f.family_name} onChange={set("family_name")} placeholder="선" />
        </div>
        <div className="sstoa-field">
          <label>이름 *</label>
          <input className="sstoa-input" required value={f.given_name} onChange={set("given_name")} placeholder="성태" />
        </div>
        <div className="sstoa-field">
          <label>직함</label>
          <input className="sstoa-input" value={f.title || ""} onChange={set("title")} placeholder="대표" />
        </div>
        <div className="sstoa-field">
          <label>주소 이름 *</label>
          <input className="sstoa-input" required value={f.slug} onChange={set("slug")} placeholder="terry" />
          <p className="sstoa-hint">{cardUrl(f.slug || "<이름>")}</p>
        </div>
        <div className="sstoa-field">
          <label>휴대폰 *</label>
          <input className="sstoa-input" required value={f.phone || ""} onChange={set("phone")} placeholder="010-0000-0000" />
        </div>
        <div className="sstoa-field">
          <label>이메일 *</label>
          <input className="sstoa-input" required type="email" value={f.email || ""} onChange={set("email")} />
        </div>
      </div>
      <label className="sstoa-check" style={{ marginTop: 10 }}>
        <input type="checkbox" checked={f.active} onChange={set("active")} /> 공개 (끄면 명함 주소가 열리지 않습니다)
      </label>
      {initial.id && initial.slug !== f.slug && (
        <p className="sstoa-hint">주소 이름을 바꾸면 이미 인쇄한 QR · NFC 의 주소는 더 이상 열리지 않습니다.</p>
      )}
      <div className="sstoa-actions" style={{ marginTop: 12 }}>
        <button className="sstoa-btn sstoa-btn--primary" disabled={busy}>저장</button>
        {onCancel && <button type="button" className="sstoa-btn" onClick={onCancel} disabled={busy}>취소</button>}
      </div>
    </form>
  );
}

function CardRow({ card, onChanged, onError }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);
  const url = cardUrl(card.slug);

  async function run(fn) {
    setBusy(true);
    try { await fn(); } catch (err) { onError(err.message); } finally { setBusy(false); }
  }

  const save = (f) => run(async () => {
    await api.post(`/cards/${card.id}`, f);
    setEditing(false);
    await onChanged();
  });

  const uploadPhoto = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(async () => { await api.upload(`/cards/${card.id}/photo`, fd); await onChanged(); });
  };

  const removePhoto = () => {
    if (!window.confirm("프로필 사진을 지울까요? 명함에는 성(이니셜)이 대신 보입니다.")) return;
    run(async () => { await api.del(`/cards/${card.id}/photo`); await onChanged(); });
  };

  const remove = () => {
    if (!window.confirm(`${card.family_name}${card.given_name} 명함을 삭제할까요? 인쇄한 QR · NFC 주소도 더 이상 열리지 않습니다.`)) return;
    run(async () => { await api.del(`/cards/${card.id}`); await onChanged(); });
  };

  return (
    <div className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ width: 84, height: 84, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "var(--s-bg)",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700, color: "var(--s-fg-mute)" }}>
          {card.photo ? <img src={card.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : card.family_name}
        </div>

        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>
            {card.family_name}{card.given_name}{" "}
            <span style={{ fontWeight: 500, color: "var(--s-fg-mute)", fontSize: 14 }}>{card.title}</span>{" "}
            {!card.active && <span className="sstoa-tag sstoa-tag--gray">비공개</span>}
          </div>
          <div style={{ fontSize: 13.5, color: "var(--s-fg-dim)", marginTop: 4 }}>{card.phone} · {card.email}</div>
          <div style={{ fontSize: 13.5, marginTop: 8, wordBreak: "break-all" }}>
            <a href={url} target="_blank" rel="noopener noreferrer" className="sstoa-link">{url}</a>
          </div>

          <div className="sstoa-actions" style={{ marginTop: 12, flexWrap: "wrap" }}>
            <button className="sstoa-btn sstoa-btn--sm" onClick={async () => { if (await copy(url)) { setCopied(true); setTimeout(() => setCopied(false), 1500); } }}>
              {copied ? "복사됨" : "주소 복사"}
            </button>
            <a className="sstoa-btn sstoa-btn--sm" href={`/api/cards/${card.slug}/qr.svg?dl=1`}>QR (SVG · 인쇄용)</a>
            <a className="sstoa-btn sstoa-btn--sm" href={`/api/cards/${card.slug}/qr.png?dl=1`}>QR (PNG)</a>
            <a className="sstoa-btn sstoa-btn--sm" href={`/api/cards/${card.slug}.vcf`}>연락처 파일</a>
            <button className="sstoa-btn sstoa-btn--sm" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>
              {card.photo ? "사진 바꾸기" : "사진 올리기"}
            </button>
            {card.photo && <button className="sstoa-btn sstoa-btn--sm" onClick={removePhoto} disabled={busy}>사진 삭제</button>}
            <button className="sstoa-btn sstoa-btn--sm" onClick={() => setEditing((v) => !v)} disabled={busy}>수정</button>
            <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={remove} disabled={busy}>삭제</button>
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" hidden onChange={uploadPhoto} />
          </div>
          {!card.active && <p className="sstoa-hint">비공개 명함은 QR · 연락처 파일도 열리지 않습니다.</p>}

          {editing && <CardForm initial={card} onSave={save} onCancel={() => setEditing(false)} busy={busy} />}
        </div>

        {card.active && (
          <img src={`/api/cards/${card.slug}/qr.svg`} alt={`${card.slug} 명함 QR`}
               style={{ width: 110, height: 110, background: "#fff", borderRadius: 6, flexShrink: 0 }} />
        )}
      </div>
    </div>
  );
}

export default function CardsAdmin() {
  const [cards, setCards] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    try {
      setCards(await api.get("/cards"));
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => { load(); }, []);

  async function create(f) {
    setBusy(true);
    setError("");
    try {
      await api.post("/cards", f);
      setAdding(false);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sstoa" className="sstoa-back">← 홈</Link>
      </div>

      <div className="sstoa-topbar">
        <div>
          <h1 className="sstoa-title">명함 관리</h1>
          <p className="sstoa-sub">
            디지털 명함 주소 · QR 코드 · 연락처 파일을 관리합니다. 사진은 정사각형으로 잘려 저장됩니다.
          </p>
        </div>
        {!adding && <button className="sstoa-btn sstoa-btn--primary" onClick={() => setAdding(true)}>새 명함</button>}
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      {adding && (
        <div className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 14 }}>
          <strong>새 명함</strong>
          <CardForm initial={EMPTY} onSave={create} onCancel={() => setAdding(false)} busy={busy} />
        </div>
      )}

      {cards === null ? (
        <div className="sstoa-panel"><div className="sstoa-empty">불러오는 중…</div></div>
      ) : cards.length === 0 ? (
        <div className="sstoa-panel"><div className="sstoa-empty">등록된 명함이 없습니다.</div></div>
      ) : (
        cards.map((c) => <CardRow key={c.id} card={c} onChanged={load} onError={setError} />)
      )}
    </div>
  );
}
